import { RepositoryError } from './RepositoryError';
/**
 * Frontend-owned, camelCase identifier for a business-rule violation that the
 * UI must translate. Backend codes (e.g. Lighthouse `extensions.code`) are
 * mapped to this union at the repository boundary; frontend code may also
 * throw `BusinessValidationError`s with codes that have no backend origin.
 *
 * Add new codes here as the UI starts to react to them — never auto-generate
 * from the backend enum. The set is intentionally narrow so consumers know
 * every code they exhaustively switch on.
 */
export const BusinessValidationCode = {
    vehicleAlreadyExists: 'vehicleAlreadyExists',
    vehicleInUse: 'vehicleInUse',
    parkingVehicleAlreadyExists: 'parkingVehicleAlreadyExists',
    parkingVehicleLimitReached: 'parkingVehicleLimitReached',
    billingHasActiveReservations: 'billingHasActiveReservations',
    billingHasAssignedParkings: 'billingHasAssignedParkings',
    defaultBillingDeletionNotAllowed: 'defaultBillingDeletionNotAllowed',
    defaultBillingAlreadyExists: 'defaultBillingAlreadyExists',
    businessBillingRequiresVatNumber: 'businessBillingRequiresVatNumber',
    controllerBillingNotPersonal: 'controllerBillingNotPersonal',
    controllerBillingStudentDataMissing: 'controllerBillingStudentDataMissing',
    authorizationCodeDoesNotExist: 'authorizationCodeDoesNotExist',
    emailCodeLoginRequired: 'emailCodeLoginRequired',
    passwordLoginRequired: 'passwordLoginRequired',
    passwordLoginNotAllowed: 'passwordLoginNotAllowed',
    loginPasswordMissing: 'loginPasswordMissing',
    invalidLoginCredentials: 'invalidLoginCredentials',
    reverificationRequired: 'reverificationRequired',
    spotTaken: 'spotTaken',
    noAvailableSpot: 'noAvailableSpot',
    waitingForPayment: 'waitingForPayment',
    paymentFailed: 'paymentFailed',
    parkingNotFound: 'parkingNotFound',
    spotNotFound: 'spotNotFound',
    activeReservationExists: 'activeReservationExists',
    paymentGatewayUnavailable: 'paymentGatewayUnavailable',
    paymentMethodRequired: 'paymentMethodRequired',
    paynowClausesUnavailable: 'paynowClausesUnavailable',
    userIsNotTemporary: 'userIsNotTemporary',
    userEmailAlreadyTaken: 'userEmailAlreadyTaken',
    referralCodeNotFound: 'referralCodeNotFound',
    userCannotReferThemselves: 'userCannotReferThemselves',
    userIsAlreadyPartner: 'userIsAlreadyPartner',
    userIsAlreadyReferred: 'userIsAlreadyReferred',
    reservationFailedDueToSpotTaken: 'reservationFailedDueToSpotTaken',
    reservationFailedDueToNoAvailableSpot: 'reservationFailedDueToNoAvailableSpot',
    reservationFailedDueToFutureReservationUnavailable: 'reservationFailedDueToFutureReservationUnavailable',
    reservationFailedDueToWaitingForPayment: 'reservationFailedDueToWaitingForPayment',
    reservationFailedDueToPaymentFailed: 'reservationFailedDueToPaymentFailed',
    reservationFailedDueToParkingNotFound: 'reservationFailedDueToParkingNotFound',
    reservationFailedDueToSpotNotFound: 'reservationFailedDueToSpotNotFound',
    reservationFailedDueToPendingReservationExists: 'reservationFailedDueToPendingReservationExists',
    reservationFailedDueToPendingExtensionExists: 'reservationFailedDueToPendingExtensionExists',
    reservationFailedDueToStepCompleted: 'reservationFailedDueToStepCompleted',
    reservationFailedDueToReservationNotFound: 'reservationFailedDueToReservationNotFound',
    reservationFailedDueToReservationOwnershipMismatch: 'reservationFailedDueToReservationOwnershipMismatch',
    reservationFailedDueToReservationLockExpired: 'reservationFailedDueToReservationLockExpired',
    reservationFailedDueToReservationNotPending: 'reservationFailedDueToReservationNotPending',
    reservationFailedDueToReservationUserAlreadyAssigned: 'reservationFailedDueToReservationUserAlreadyAssigned',
    reservationFailedDueToVehicleNotFound: 'reservationFailedDueToVehicleNotFound',
    reservationFailedDueToVehicleOwnershipMismatch: 'reservationFailedDueToVehicleOwnershipMismatch',
    reservationFailedDueToVehicleLimitReached: 'reservationFailedDueToVehicleLimitReached',
    reservationFailedDueToRegistrationNumberAlreadyAdded: 'reservationFailedDueToRegistrationNumberAlreadyAdded',
    reservationFailedDueToVehicleSealed: 'reservationFailedDueToVehicleSealed',
    reservationFailedDueToLastVehicle: 'reservationFailedDueToLastVehicle',
    reservationFailedDueToBillingNotFound: 'reservationFailedDueToBillingNotFound',
    reservationFailedDueToBillingOwnershipMismatch: 'reservationFailedDueToBillingOwnershipMismatch',
    reservationFailedDueToReservationNotReassignable: 'reservationFailedDueToReservationNotReassignable',
    reservationFailedDueToRevolvingPeriodUnavailable: 'reservationFailedDueToRevolvingPeriodUnavailable',
    reservationFailedDueToOther: 'reservationFailedDueToOther',
    reservationNotActive: 'reservationNotActive',
    reservationSpanNotActive: 'reservationSpanNotActive',
    reservationSpanNotPayable: 'reservationSpanNotPayable',
    reservationSpanPaymentLinkInvalid: 'reservationSpanPaymentLinkInvalid',
    reservationNotRevolving: 'reservationNotRevolving',
    reservationAlreadyRevolving: 'reservationAlreadyRevolving',
    reservationRevolvingHoldConflict: 'reservationRevolvingHoldConflict',
    reservationRevolvingNotAllowedByPriceRule: 'reservationRevolvingNotAllowedByPriceRule',
    reservationRevolvingSpotMissing: 'reservationRevolvingSpotMissing',
    reservationRevolvingStatusNotAllowed: 'reservationRevolvingStatusNotAllowed',
    reservationExtensionEndDateInvalid: 'reservationExtensionEndDateInvalid',
    reservationHorizonExceeded: 'reservationHorizonExceeded',
    reservationCancelFailed: 'reservationCancelFailed',
    invalidTicketOwnership: 'invalidTicketOwnership',
    parkingPointTooFar: 'parkingPointTooFar',
    parkingPointNoBasicAddress: 'parkingPointNoBasicAddress',
    vehicleAlreadyReported: 'vehicleAlreadyReported',
};
/**
 * A user-facing business-rule violation. Carries a frontend-owned `code` that
 * the consuming component translates — the backend's human message is
 * intentionally ignored. Optional `field` lets forms render the message inline
 * next to a specific input; optional `context` exposes structured payload from
 * the source (e.g. backend `extensions.context`) for richer UX.
 *
 * Excluded from Sentry, retries, and the global `getErrorMessage` toast text
 * (which falls back to the caller-provided string), so a missing translation
 * never leaks an untranslated message to the user.
 */
export class BusinessValidationError extends RepositoryError {
    code;
    field;
    context;
    constructor(code, options) {
        super(code, options?.cause !== undefined ? { cause: options.cause } : undefined);
        this.name = 'BusinessValidationError';
        this.code = code;
        this.field = options?.field ?? null;
        this.context = options?.context ?? null;
    }
}
